import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import type { Location, OperatingHours, DaySchedule } from '@lds-efsp/shared-contracts';
import {
  SetupDetailDrawer,
  SetupFilterBar,
  SetupDataTable,
  StatusBadge,
  SetupItemPreviewPopover,
} from '../components';
import FormInput, { FormSelect, FormCheckbox } from '../components/FormInput';
import { useApi } from '../../../api/useApi';
import type { ZipcodeRecord, LocationLabCourtRecord } from '../data/location-shared';
import AreasTab from './AreasTab';
import ZoneLookupTab from './ZoneLookupTab';

// ── Types ──

type CourtRecord = Location;

interface ServiceFlags {
  filing: boolean;
  documentRetrieval: boolean;
  courtesyCopy: boolean;
  countyRecorders: boolean;
  secretaryState: boolean;
  efiling: boolean;
  serve: boolean;
  writ: boolean;
}

interface CourtFormData {
  locationName: string;
  jurisdiction: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  level: string;
  courtType: string;
  pricingZone: string;
  offerToAffiliates: boolean;
  isActive: boolean;
  serviceFlags: ServiceFlags;
  operatingHours: OperatingHours;
  locationTimeToDeliver: number;
  officeTimeToDeliver: number;
}

// ── Constants ──

const DEFAULT_DAY: DaySchedule = { open: '08:30 AM', close: '04:00 PM', deadline: '02:00 PM', efile: '11:50 PM' };

const DEFAULT_HOURS: OperatingHours = {
  monday: { ...DEFAULT_DAY },
  tuesday: { ...DEFAULT_DAY },
  wednesday: { ...DEFAULT_DAY },
  thursday: { ...DEFAULT_DAY },
  friday: { ...DEFAULT_DAY },
};

const EMPTY_FLAGS: ServiceFlags = {
  filing: false, documentRetrieval: false, courtesyCopy: false, countyRecorders: false,
  secretaryState: false, efiling: false, serve: false, writ: false,
};

const levelOptions = [
  { value: '', label: 'All Levels' },
  { value: 'Federal', label: 'Federal' },
  { value: 'State', label: 'State' },
  { value: 'Sheriff', label: 'Sheriff' },
];

const levelEditOptions = [
  { value: 'Federal', label: 'Federal' },
  { value: 'State', label: 'State' },
  { value: 'Sheriff', label: 'Sheriff' },
];

const typeOptions = [
  { value: '', label: 'All Types' },
  { value: 'District Court', label: 'District Court' },
  { value: 'Circuit Court', label: 'Circuit Court' },
  { value: 'Bankruptcy Court', label: 'Bankruptcy Court' },
  { value: 'Superior', label: 'Superior' },
  { value: 'County Recorder', label: 'County Recorder' },
  { value: 'Family', label: 'Family' },
  { value: 'Appellate', label: 'Appellate' },
  { value: 'WCAB', label: 'WCAB' },
  { value: 'County', label: 'County' },
  { value: 'State', label: 'State' },
];

const typeEditOptions = typeOptions.filter(o => o.value !== '');

const zoneFilterOptions = [
  { value: '', label: 'All Zones' },
  { value: 'Remote Court', label: 'Remote' },
  { value: 'Suburban Court', label: 'Suburban' },
  { value: 'Urban Court', label: 'Urban' },
  { value: 'eFiling: California', label: 'eFiling' },
];

const zoneEditOptions = [
  { value: 'Remote Court', label: 'Remote Court' },
  { value: 'Suburban Court', label: 'Suburban Court' },
  { value: 'Urban Court', label: 'Urban Court' },
  { value: 'eFiling: California', label: 'eFiling: California' },
  { value: 'Nationwide Court', label: 'Nationwide Court' },
];

const statusFilterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const serviceFilterOptions = [
  { value: '', label: 'All Services' },
  { value: 'filing', label: 'Filing' },
  { value: 'efiling', label: 'E-Filing' },
  { value: 'documentRetrieval', label: 'Doc Retrieval' },
  { value: 'courtesyCopy', label: 'Courtesy Copy' },
  { value: 'serve', label: 'Serve' },
  { value: 'writ', label: 'Writ' },
  { value: 'countyRecorders', label: 'County Rec' },
  { value: 'secretaryState', label: 'Sec State' },
];

// Service chip config
const SERVICE_CONFIG: { key: keyof ServiceFlags; label: string; abbr: string; bg: string; text: string }[] = [
  { key: 'filing', label: 'Filing', abbr: 'Fil', bg: 'bg-green-100', text: 'text-green-700' },
  { key: 'efiling', label: 'E-Filing', abbr: 'E-Fil', bg: 'bg-blue-100', text: 'text-blue-700' },
  { key: 'documentRetrieval', label: 'Document Retrieval', abbr: 'Ret', bg: 'bg-purple-100', text: 'text-purple-700' },
  { key: 'courtesyCopy', label: 'Courtesy Copy', abbr: 'CC', bg: 'bg-teal-100', text: 'text-teal-700' },
  { key: 'countyRecorders', label: 'County Recorders', abbr: 'CRec', bg: 'bg-amber-100', text: 'text-amber-700' },
  { key: 'secretaryState', label: 'Secretary of State', abbr: 'SoS', bg: 'bg-orange-100', text: 'text-orange-700' },
  { key: 'serve', label: 'Service of Process', abbr: 'SOP', bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { key: 'writ', label: 'Writ', abbr: 'Writ', bg: 'bg-rose-100', text: 'text-rose-700' },
];

// ── Helpers ──

function countActiveServices(flags: ServiceFlags): number {
  return Object.values(flags).filter(Boolean).length;
}

function zoneColor(zone: string): { bg: string; text: string } {
  if (zone.includes('Urban')) return { bg: 'bg-green-100', text: 'text-green-700' };
  if (zone.includes('Suburban')) return { bg: 'bg-blue-100', text: 'text-blue-700' };
  if (zone.includes('eFiling')) return { bg: 'bg-violet-100', text: 'text-violet-700' };
  return { bg: 'bg-gray-100', text: 'text-gray-700' }; // Remote
}

function zoneShortName(zone: string): string {
  if (zone.includes('Urban')) return 'Urban';
  if (zone.includes('Suburban')) return 'Suburban';
  if (zone.includes('eFiling')) return 'eFiling';
  if (zone.includes('Nationwide')) return 'National';
  return 'Remote';
}

function toFormData(court: CourtRecord): CourtFormData {
  return {
    locationName: court.locationName || '',
    jurisdiction: court.Jurisdiction,
    address: court.Address,
    city: court.City,
    state: court.State,
    zip: court.zip || '',
    county: court.County,
    level: court.level || 'State',
    courtType: court.courtType || '',
    pricingZone: court['Pricing Zone'] || 'Remote Court',
    offerToAffiliates: court['Offer to Affiliates?'] === 'Yes',
    isActive: court.isActive,
    serviceFlags: { ...court.serviceFlags },
    operatingHours: court.operatingHours ? JSON.parse(JSON.stringify(court.operatingHours)) : { ...DEFAULT_HOURS },
    locationTimeToDeliver: court.locationTimeToDeliver ?? 120,
    officeTimeToDeliver: court.officeTimeToDeliver ?? 0,
  };
}

function emptyFormData(): CourtFormData {
  return {
    locationName: '',
    jurisdiction: '',
    address: '',
    city: '',
    state: 'CA',
    zip: '',
    county: '',
    level: 'State',
    courtType: 'Superior',
    pricingZone: 'Remote Court',
    offerToAffiliates: false,
    isActive: true,
    serviceFlags: { ...EMPTY_FLAGS },
    operatingHours: JSON.parse(JSON.stringify(DEFAULT_HOURS)),
    locationTimeToDeliver: 120,
    officeTimeToDeliver: 0,
  };
}

function formToPayload(form: CourtFormData) {
  return {
    county: form.county,
    jurisdiction: form.jurisdiction,
    address: form.address,
    city: form.city,
    state: form.state,
    pricingZone: form.pricingZone,
    offerToAffiliates: form.offerToAffiliates,
    locationName: form.locationName || undefined,
    zip: form.zip || undefined,
    level: form.level || undefined,
    courtType: form.courtType || undefined,
    isActive: form.isActive,
    serviceFlags: form.serviceFlags,
    operatingHours: form.operatingHours,
    locationTimeToDeliver: form.locationTimeToDeliver,
    officeTimeToDeliver: form.officeTimeToDeliver,
  };
}

// ── Sub-components ──

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri' };

/** Visual hours timeline */
const HoursTimeline: React.FC<{ hours: OperatingHours; brand: BrandConfig }> = ({ hours, brand }) => {
  const parseTime = (t: string): number => {
    const m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return 0;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const period = m[3].toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + min;
  };

  const START = 6 * 60; // 6 AM
  const END = 24 * 60; // midnight
  const RANGE = END - START;
  const pct = (t: number) => Math.max(0, Math.min(100, ((t - START) / RANGE) * 100));

  return (
    <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: brand.colors.bgLight }}>
      <div className="text-xs font-medium mb-3" style={{ color: brand.colors.textLight }}>Weekly Hours Overview</div>
      {/* Time axis */}
      <div className="flex text-[10px] mb-1" style={{ color: brand.colors.textLight }}>
        {[6, 8, 10, 12, 14, 16, 18, 20, 22, 24].map(h => (
          <div key={h} style={{ position: 'absolute', left: `${pct(h * 60)}%` }} className="relative">
            {h <= 12 ? `${h}${h < 12 ? 'a' : 'p'}` : `${h - 12}p`}
          </div>
        ))}
      </div>
      <div className="space-y-1.5 mt-1">
        {DAYS.map(day => {
          const d = hours[day];
          if (!d) return (
            <div key={day} className="flex items-center gap-2">
              <span className="w-8 text-xs font-medium" style={{ color: brand.colors.textLight }}>{DAY_LABELS[day]}</span>
              <div className="flex-1 h-5 rounded bg-gray-100 relative" />
            </div>
          );
          const openPct = pct(parseTime(d.open));
          const closePct = pct(parseTime(d.close));
          const deadlinePct = d.deadline ? pct(parseTime(d.deadline)) : null;
          return (
            <div key={day} className="flex items-center gap-2">
              <span className="w-8 text-xs font-medium" style={{ color: brand.colors.textLight }}>{DAY_LABELS[day]}</span>
              <div className="flex-1 h-5 rounded bg-gray-100 relative overflow-hidden">
                <div
                  className="absolute top-0 h-full rounded"
                  style={{
                    left: `${openPct}%`,
                    width: `${closePct - openPct}%`,
                    backgroundColor: `${brand.colors.primary}30`,
                    borderLeft: `2px solid ${brand.colors.primary}`,
                    borderRight: `2px solid ${brand.colors.primary}`,
                  }}
                />
                {deadlinePct !== null && (
                  <div
                    className="absolute top-0 h-full"
                    style={{ left: `${deadlinePct}%`, width: '2px', backgroundColor: '#f59e0b' }}
                    title={`Filing deadline: ${d.deadline}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-4 mt-2 text-[10px]" style={{ color: brand.colors.textLight }}>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded" style={{ backgroundColor: `${brand.colors.primary}30` }} /> Open
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded bg-amber-400" /> Deadline
        </span>
      </div>
    </div>
  );
};

// ── Skeleton rows ──
const SkeletonRow: React.FC = () => (
  <div className="flex gap-4 py-3 px-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-48" />
    <div className="h-4 bg-gray-200 rounded w-40" />
    <div className="h-4 bg-gray-200 rounded w-16" />
    <div className="h-4 bg-gray-200 rounded w-24" />
    <div className="h-4 bg-gray-200 rounded w-20" />
    <div className="h-4 bg-gray-200 rounded w-16" />
    <div className="h-4 bg-gray-200 rounded w-32" />
    <div className="h-4 bg-gray-200 rounded w-16" />
  </div>
);

// ── Props ──

interface SetupLocationsProps {
  brand: BrandConfig;
  Icons: IconsType;
}

type DrawerTab = 'details' | 'hours' | 'delivery';
type LocationsMainTab = 'courts' | 'areas' | 'zoneLookup';

// ════════════════════════════════════════════
// ██  MAIN COMPONENT
// ════════════════════════════════════════════

const SetupLocations: React.FC<SetupLocationsProps> = ({ brand, Icons }) => {
  const { get, post, patch, del } = useApi();

  // ── Main Tab State ──
  const [activeMainTab, setActiveMainTab] = useState<LocationsMainTab>('courts');
  const [zipcodes, setZipcodes] = useState<ZipcodeRecord[]>([]);
  const [labCourts, setLabCourts] = useState<LocationLabCourtRecord[]>([]);

  // ── State ──
  const [courts, setCourts] = useState<CourtRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');

  const [selectedCourt, setSelectedCourt] = useState<CourtRecord | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<CourtFormData>(() => emptyFormData());
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<DrawerTab>('details');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  // ── Data Fetch ──

  const fetchCourts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await get<{ data: CourtRecord[]; total: number }>('/api/admin/setup/location-lab/courts');
      setCourts(data.data || []);
    } catch (error) {
      console.error('Failed to fetch courts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [get]);

  const fetchZipcodes = useCallback(async () => {
    try {
      const data = await get<{ data: ZipcodeRecord[]; total: number }>('/api/admin/setup/location-lab/zipcodes');
      setZipcodes(data.data || []);
    } catch (error) {
      console.error('Failed to fetch zipcodes:', error);
    }
  }, [get]);

  const fetchLabCourts = useCallback(async () => {
    try {
      const data = await get<{ data: LocationLabCourtRecord[]; total: number }>('/api/admin/setup/location-lab/courts');
      setLabCourts(data.data || []);
    } catch (error) {
      console.error('Failed to fetch lab courts:', error);
    }
  }, [get]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchCourts();
    fetchZipcodes();
    fetchLabCourts();
  }, [fetchCourts, fetchZipcodes, fetchLabCourts]);

  // ── Filtering ──

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return courts.filter(c => {
      if (q) {
        const haystack = [c.locationName, c.Jurisdiction, c.County, c.City, c.zip].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (statusFilter !== 'all') {
        if (statusFilter === 'active' && !c.isActive) return false;
        if (statusFilter === 'inactive' && c.isActive) return false;
      }
      if (levelFilter && c.level !== levelFilter) return false;
      if (typeFilter && c.courtType !== typeFilter) return false;
      if (zoneFilter && c['Pricing Zone'] !== zoneFilter) return false;
      if (serviceFilter && !c.serviceFlags[serviceFilter as keyof ServiceFlags]) return false;
      return true;
    });
  }, [courts, search, statusFilter, levelFilter, typeFilter, zoneFilter, serviceFilter]);

  const paged = useMemo(() => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [filtered, page]);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  // Reset page when filters change
  /* eslint-disable @eslint-react/set-state-in-effect -- reset pagination on filter change */
  // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
  useEffect(() => { setPage(0); }, [search, statusFilter, levelFilter, typeFilter, zoneFilter, serviceFilter]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  // ── Summary Stats ──

  const stats = useMemo(() => {
    const total = courts.length;
    const federal = courts.filter(c => c.level === 'Federal').length;
    const state = courts.filter(c => c.level === 'State').length;
    const sheriff = courts.filter(c => c.level === 'Sheriff').length;
    const remote = courts.filter(c => c['Pricing Zone'].includes('Remote')).length;
    const suburban = courts.filter(c => c['Pricing Zone'].includes('Suburban')).length;
    const urban = courts.filter(c => c['Pricing Zone'].includes('Urban')).length;
    const efiling = courts.filter(c => c['Pricing Zone'].includes('eFiling')).length;
    return { total, federal, state, sheriff, remote, suburban, urban, efiling };
  }, [courts]);

  // ── Handlers ──

  const handleRowClick = (court: CourtRecord) => {
    setSelectedCourt(court);
    setFormData(toFormData(court));
    setIsCreating(false);
    setActiveTab('details');
    setShowDeleteConfirm(false);
    setIsDrawerOpen(true);
  };

  const handleAdd = () => {
    setSelectedCourt(null);
    setFormData(emptyFormData());
    setIsCreating(true);
    setActiveTab('details');
    setShowDeleteConfirm(false);
    setIsDrawerOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value, 10) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const toggleService = (key: keyof ServiceFlags) => {
    setFormData(prev => ({
      ...prev,
      serviceFlags: { ...prev.serviceFlags, [key]: !prev.serviceFlags[key] },
    }));
  };

  const handleHoursChange = (day: typeof DAYS[number], field: keyof DaySchedule, value: string) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: { ...(prev.operatingHours[day] || DEFAULT_DAY), [field]: value },
      },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = formToPayload(formData);
      if (isCreating) {
        await post('/api/admin/setup/location-lab/courts', payload);
      } else if (selectedCourt) {
        await patch(`/api/admin/setup/location-lab/courts/${selectedCourt.id}`, payload);
      }
      setIsDrawerOpen(false);
      fetchCourts();
    } catch (error) {
      console.error('Failed to save court:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCourt) return;
    setIsSaving(true);
    try {
      await del(`/api/admin/setup/location-lab/courts/${selectedCourt.id}`);
      setIsDrawerOpen(false);
      fetchCourts();
    } catch (error) {
      console.error('Failed to delete court:', error);
    } finally {
      setIsSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  // ── Table Columns ──

  const columns = [
    {
      key: 'locationName',
      header: 'Name',
      render: (court: CourtRecord) => (
        <div className="flex items-center gap-2">
          <span className="font-medium truncate max-w-[240px]" style={{ color: brand.colors.link }}>
            {court.locationName || court.Jurisdiction}
          </span>
          <SetupItemPreviewPopover
            brand={brand}
            title={court.locationName || court.Jurisdiction}
            fields={[
              { label: 'Level', value: court.level || '-' },
              { label: 'Type', value: court.courtType || '-' },
              { label: 'County', value: court.County },
              { label: 'Zone', value: zoneShortName(court['Pricing Zone']) },
              { label: 'Services', value: `${countActiveServices(court.serviceFlags)} of 8` },
              { label: 'Delivery', value: court.locationTimeToDeliver != null ? `${court.locationTimeToDeliver} min` : '-' },
              { label: 'Status', value: court.isActive ? 'Active' : 'Inactive' },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'jurisdiction',
      header: 'Jurisdiction',
      render: (court: CourtRecord) => (
        <span className="text-sm truncate max-w-[200px] block" style={{ color: brand.colors.textLight }}>
          {court.Jurisdiction}
        </span>
      ),
    },
    {
      key: 'level',
      header: 'Level',
      width: '80px',
      render: (court: CourtRecord) => {
        const isF = court.level === 'Federal';
        return (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${isF ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700'}`}>
            {court.level || '-'}
          </span>
        );
      },
    },
    {
      key: 'courtType',
      header: 'Type',
      width: '120px',
      render: (court: CourtRecord) => (
        <span className="text-xs" style={{ color: brand.colors.textLight }}>{court.courtType || '-'}</span>
      ),
    },
    {
      key: 'county',
      header: 'County',
      width: '120px',
      render: (court: CourtRecord) => (
        <span className="text-sm" style={{ color: brand.colors.text }}>{court.County}</span>
      ),
    },
    {
      key: 'zone',
      header: 'Zone',
      width: '100px',
      render: (court: CourtRecord) => {
        const zc = zoneColor(court['Pricing Zone']);
        return (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${zc.bg} ${zc.text}`}>
            {zoneShortName(court['Pricing Zone'])}
          </span>
        );
      },
    },
    {
      key: 'services',
      header: 'Services',
      width: '160px',
      render: (court: CourtRecord) => {
        const active = SERVICE_CONFIG.filter(s => court.serviceFlags[s.key]);
        const visible = active.slice(0, 4);
        const overflow = active.length - 4;
        return (
          <div className="flex flex-wrap gap-1">
            {visible.map(s => (
              <span key={s.key} className={`inline-flex px-1.5 py-0 rounded text-[10px] font-medium ${s.bg} ${s.text}`}>
                {s.abbr}
              </span>
            ))}
            {overflow > 0 && (
              <span className="inline-flex px-1.5 py-0 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                +{overflow}
              </span>
            )}
            {active.length === 0 && (
              <span className="text-xs" style={{ color: brand.colors.textLight }}>-</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '80px',
      render: (court: CourtRecord) => (
        <StatusBadge status={court.isActive ? 'active' : 'inactive'} />
      ),
    },
  ];

  // ── Tab definitions ──

  const tabs: { key: DrawerTab; label: string }[] = [
    { key: 'details', label: 'Details' },
    { key: 'hours', label: 'Operating Hours' },
    { key: 'delivery', label: 'Delivery Times' },
  ];

  // ── Main Tab Definitions ──

  const mainTabs: { key: LocationsMainTab; label: string }[] = [
    { key: 'courts', label: 'Courts' },
    { key: 'areas', label: 'Areas' },
    { key: 'zoneLookup', label: 'Zone Lookup' },
  ];

  // ── Render ──

  return (
    <div>
      {/* Main Tab Navigation */}
      <div
        className="flex border-b mb-6"
        style={{ borderColor: brand.colors.border }}
      >
        {mainTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveMainTab(tab.key)}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeMainTab === tab.key ? '' : 'border-transparent'
            }`}
            style={{
              color: activeMainTab === tab.key ? brand.colors.link : brand.colors.textLight,
              borderColor: activeMainTab === tab.key ? brand.colors.primary : 'transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Areas Tab */}
      {activeMainTab === 'areas' && (
        <AreasTab brand={brand} Icons={Icons} zipcodes={zipcodes} />
      )}

      {/* Zone Lookup Tab */}
      {activeMainTab === 'zoneLookup' && (
        <ZoneLookupTab brand={brand} zipcodes={zipcodes} courts={labCourts} />
      )}

      {/* Courts Tab - existing content unchanged */}
      {activeMainTab === 'courts' && <>
      {/* Summary Stats */}
      {!isLoading && courts.length > 0 && (
        <div
          className="flex flex-wrap gap-3 mb-4 p-3 rounded-lg"
          style={{ backgroundColor: brand.colors.bgLight }}
        >
          <StatCard label="Total Courts" value={stats.total} color={brand.colors.text} />
          <StatCard label="Federal" value={stats.federal} color="#6366f1" />
          <StatCard label="State" value={stats.state} color="#6b7280" />
          {stats.sheriff > 0 && <StatCard label="Sheriff" value={stats.sheriff} color="#92400e" />}
          <div className="w-px bg-gray-300 self-stretch" />
          <StatCard label="Remote" value={stats.remote} color="#6b7280" />
          <StatCard label="Suburban" value={stats.suburban} color="#3b82f6" />
          <StatCard label="Urban" value={stats.urban} color="#16a34a" />
          {stats.efiling > 0 && <StatCard label="eFiling" value={stats.efiling} color="#8b5cf6" />}
        </div>
      )}

      {/* Filter Bar */}
      <SetupFilterBar
        brand={brand}
        Icons={Icons}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search courts..."
        statusFilter={statusFilter}
        statusOptions={statusFilterOptions}
        onStatusChange={setStatusFilter}
        onAdd={handleAdd}
        addLabel="Add Court"
      />

      {/* Extra Filters */}
      <div className="flex flex-wrap gap-2 mb-4 -mt-2">
        <MiniSelect value={levelFilter} onChange={setLevelFilter} options={levelOptions} brand={brand} />
        <MiniSelect value={typeFilter} onChange={setTypeFilter} options={typeOptions} brand={brand} />
        <MiniSelect value={serviceFilter} onChange={setServiceFilter} options={serviceFilterOptions} brand={brand} />
        <MiniSelect value={zoneFilter} onChange={setZoneFilter} options={zoneFilterOptions} brand={brand} />
        {filtered.length !== courts.length && (
          <span className="text-xs self-center ml-1" style={{ color: brand.colors.textLight }}>
            {filtered.length} of {courts.length} courts
          </span>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="border rounded-lg overflow-hidden" style={{ borderColor: brand.colors.border }}>
          {Array.from({ length: 10 }).map((_, i) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton placeholders
            <SkeletonRow key={`skeleton-${i}`} />
          ))}
        </div>
      ) : (
        <SetupDataTable
          brand={brand}
          Icons={Icons}
          columns={columns}
          data={paged}
          isLoading={false}
          onRowClick={handleRowClick}
          emptyMessage="No courts match your filters"
          getRowId={(court) => court.id}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs" style={{ color: brand.colors.textLight }}>
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-xs rounded border disabled:opacity-30"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 text-xs rounded border disabled:opacity-30"
              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ═══════════ Detail Drawer ═══════════ */}
      <SetupDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={isCreating ? 'New Court Location' : 'Edit Court Location'}
        subtitle={selectedCourt?.locationName || selectedCourt?.Jurisdiction}
        brand={brand}
        Icons={Icons}
        width="xl"
        footer={
          <div className="flex justify-between w-full">
            <div>
              {!isCreating && selectedCourt && (
                showDeleteConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-600">Delete this court?</span>
                    <button
                      onClick={handleDelete}
                      disabled={isSaving}
                      className="px-3 py-1.5 rounded text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-3 py-1.5 rounded text-sm border"
                      style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Delete
                  </button>
                )
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setIsDrawerOpen(false)}
                className="px-4 py-2 border rounded-lg text-sm font-medium transition-colors hover:bg-gray-50"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        }
      >
        {/* Tabs */}
        <div className="flex border-b mb-6 -mx-6 px-6" style={{ borderColor: brand.colors.border }}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeTab === tab.key ? '' : 'border-transparent'
              }`}
              style={{
                color: activeTab === tab.key ? brand.colors.link : brand.colors.textLight,
                borderColor: activeTab === tab.key ? brand.colors.primary : 'transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ Tab 1: Details ═══ */}
        {activeTab === 'details' && (
          <div className="space-y-6">
            {/* Identity */}
            <div>
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>Identity</h3>
              <FormInput brand={brand} label="Location Name" name="locationName" value={formData.locationName} onChange={handleInputChange} placeholder='e.g., NORTHERN DISTRICT, OAKLAND' />
              <FormInput brand={brand} label="Jurisdiction" name="jurisdiction" value={formData.jurisdiction} onChange={handleInputChange} required />
              <FormInput brand={brand} label="Address" name="address" value={formData.address} onChange={handleInputChange} required />
              <div className="grid grid-cols-3 gap-4">
                <FormInput brand={brand} label="City" name="city" value={formData.city} onChange={handleInputChange} required />
                <FormInput brand={brand} label="State" name="state" value={formData.state} onChange={handleInputChange} required />
                <FormInput brand={brand} label="Zip" name="zip" value={formData.zip} onChange={handleInputChange} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <FormInput brand={brand} label="County" name="county" value={formData.county} onChange={handleInputChange} required />
                <FormSelect brand={brand} label="Level" name="level" value={formData.level} onChange={handleInputChange} options={levelEditOptions} />
                <FormSelect brand={brand} label="Type" name="courtType" value={formData.courtType} onChange={handleInputChange} options={typeEditOptions} />
              </div>
            </div>

            {/* Pricing */}
            <div className="border-t pt-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>Pricing & Classification</h3>
              <div className="grid grid-cols-2 gap-4">
                <FormSelect brand={brand} label="Pricing Zone" name="pricingZone" value={formData.pricingZone} onChange={handleInputChange} options={zoneEditOptions} />
                <FormCheckbox brand={brand} label="Offer to Affiliates" name="offerToAffiliates" checked={formData.offerToAffiliates} onChange={handleInputChange} />
              </div>
            </div>

            {/* Service Capabilities */}
            <div className="border-t pt-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>Service Capabilities</h3>
              <div className="flex flex-wrap gap-2">
                {SERVICE_CONFIG.map(svc => {
                  const active = formData.serviceFlags[svc.key];
                  return (
                    <button
                      key={svc.key}
                      type="button"
                      onClick={() => toggleService(svc.key)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        active ? `${svc.bg} ${svc.text}` : 'bg-white border text-gray-400'
                      }`}
                      style={!active ? { borderColor: brand.colors.border } : undefined}
                    >
                      {svc.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status */}
            <div className="border-t pt-4" style={{ borderColor: brand.colors.border }}>
              <FormSelect
                brand={brand}
                label="Status"
                name="isActive"
                value={formData.isActive ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
              />
            </div>
          </div>
        )}

        {/* ═══ Tab 2: Operating Hours ═══ */}
        {activeTab === 'hours' && (
          <div className="space-y-6">
            <HoursTimeline hours={formData.operatingHours} brand={brand} />

            {/* Editable grid */}
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${brand.colors.border}` }}>
                    <th className="text-left py-2 px-2 text-xs uppercase tracking-wider font-medium" style={{ color: brand.colors.textLight }}>Day</th>
                    <th className="text-left py-2 px-2 text-xs uppercase tracking-wider font-medium" style={{ color: brand.colors.textLight }}>Open</th>
                    <th className="text-left py-2 px-2 text-xs uppercase tracking-wider font-medium" style={{ color: brand.colors.textLight }}>Close</th>
                    <th className="text-left py-2 px-2 text-xs uppercase tracking-wider font-medium" style={{ color: brand.colors.textLight }}>Filing Deadline</th>
                    <th className="text-left py-2 px-2 text-xs uppercase tracking-wider font-medium" style={{ color: brand.colors.textLight }}>E-Filing Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {DAYS.map(day => {
                    const d = formData.operatingHours[day] || DEFAULT_DAY;
                    const monDay = formData.operatingHours.monday || DEFAULT_DAY;
                    const differs = day !== 'monday' && (d.open !== monDay.open || d.close !== monDay.close || d.deadline !== monDay.deadline);
                    return (
                      <tr
                        key={day}
                        style={{
                          borderBottom: `1px solid ${brand.colors.border}`,
                          borderLeft: differs ? '3px solid #f59e0b' : '3px solid transparent',
                        }}
                      >
                        <td className="py-2 px-2 font-medium capitalize" style={{ color: brand.colors.text }}>
                          {day}
                        </td>
                        {(['open', 'close', 'deadline', 'efile'] as const).map(field => (
                          <td key={field} className="py-1 px-2">
                            <input
                              type="text"
                              value={d[field]}
                              onChange={(e) => handleHoursChange(day, field, e.target.value)}
                              className="w-full px-2 py-1 border rounded text-sm"
                              style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ═══ Tab 3: Delivery Times ═══ */}
        {activeTab === 'delivery' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>Location Delivery</h3>
              <div className="flex items-center gap-2">
                <FormInput
                  brand={brand}
                  label="Time to Deliver to this Location"
                  name="locationTimeToDeliver"
                  type="number"
                  value={formData.locationTimeToDeliver}
                  onChange={handleInputChange}
                />
                <span className="text-sm mt-5" style={{ color: brand.colors.textLight }}>minutes</span>
              </div>
            </div>

            <div className="border-t pt-4" style={{ borderColor: brand.colors.border }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: brand.colors.text }}>Office Delivery</h3>
              <div className="flex items-center gap-2">
                <FormInput
                  brand={brand}
                  label="Office Time to Deliver"
                  name="officeTimeToDeliver"
                  type="number"
                  value={formData.officeTimeToDeliver}
                  onChange={handleInputChange}
                />
                <span className="text-sm mt-5" style={{ color: brand.colors.textLight }}>minutes</span>
              </div>
            </div>
          </div>
        )}
      </SetupDetailDrawer>
      </>}
    </div>
  );
};

// ── Utility Components ──

const StatCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div className="flex items-center gap-2 px-3 py-1.5">
    <span className="text-lg font-semibold" style={{ color }}>{value}</span>
    <span className="text-xs text-gray-500">{label}</span>
  </div>
);

const MiniSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  brand: BrandConfig;
}> = ({ value, onChange, options, brand }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="px-2 py-1 text-xs border rounded"
    style={{ borderColor: brand.colors.border, color: brand.colors.text }}
  >
    {options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export default SetupLocations;
